import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, PageHeader, Pill } from "@/components/ui";
import { confirmPopup, popup } from "@/components/popup";
import { flag } from "./Nodes";

interface DomainRecord {
  ip: string;
  id: string;
  node_id: number | null;
  node_name: string | null;
  country: string | null;
  is_node: boolean;
}
interface DomainRow {
  name: string;
  zone_id: string;
  zone_name: string | null;
  records: DomainRecord[];
  proxied: boolean;
  endpoint_id: number | null;
  endpoint_name: string | null;
  kind: string | null;
  expected_ips: string[];
  status: "ok" | "mismatch" | "unbound" | "orphan" | "external";
}

type Status = DomainRow["status"];

// order — приоритет в группировке (проблемы сверху). emoji/label/tone — для UI.
const STATUS: Record<Status, { label: string; emoji: string; order: number; tone: string; hint: string }> = {
  mismatch: { label: "не туда", emoji: "⚠️", order: 0, tone: "text-amber-300 bg-amber-500/10 ring-amber-500/25", hint: "записи указывают не на текущие входы хоста" },
  orphan: { label: "сирота", emoji: "🔴", order: 1, tone: "text-red-300 bg-red-500/10 ring-red-500/30", hint: "бьёт в IP нашей ноды, но не привязан ни к какому хосту — кандидат на удаление" },
  ok: { label: "привязан", emoji: "✅", order: 2, tone: "text-emerald-300 bg-emerald-500/10 ring-emerald-500/25", hint: "A-записи совпадают с IP входов хоста" },
  unbound: { label: "нет записей", emoji: "⚪", order: 3, tone: "text-zinc-400 bg-white/[0.04] ring-white/10", hint: "хост есть, но домен не привязан" },
  external: { label: "прочее", emoji: "▫️", order: 4, tone: "text-zinc-400 bg-white/[0.04] ring-white/10", hint: "запись в нашей зоне, но не на наши ноды" },
};

type Filter = "all" | "hosts" | "mismatch" | "orphan" | "external";

export default function Domains() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const [zone, setZone] = useState<string>("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [collapsedGrp, setCollapsedGrp] = useState<Set<string>>(new Set());

  const q = useQuery<{ domains: DomainRow[]; cf_enabled: boolean }>({
    queryKey: ["domains"],
    queryFn: () => api("/api/admin/domains"),
    refetchInterval: 20000,
  });

  const del = useMutation({
    mutationFn: (p: { name: string; zone_id: string; force: boolean }) =>
      api("/api/admin/domains", { method: "DELETE", body: JSON.stringify(p) }),
    onSuccess: (r: any) => {
      popup({ title: "Удалено", body: `Снято A-записей: ${r?.deleted ?? 0}`, tone: "success" });
      qc.invalidateQueries({ queryKey: ["domains"] });
    },
    onError: (e: any) => popup({ title: "Ошибка", body: String(e?.message || e), tone: "error" }),
  });

  const rows = q.data?.domains ?? [];

  // Инвентарь по ВСЕМ записям (не зависит от фильтров) — чтобы не забыть, что где.
  const inv = useMemo(() => {
    const zones = new Set<string>();
    let recs = 0, hosts = 0, mismatch = 0, orphan = 0, external = 0, unbound = 0;
    for (const r of rows) {
      zones.add(r.zone_name || r.zone_id);
      recs += r.records.length;
      if (r.endpoint_id) hosts++;
      if (r.status === "mismatch") mismatch++;
      else if (r.status === "orphan") orphan++;
      else if (r.status === "external") external++;
      else if (r.status === "unbound") unbound++;
    }
    return { zones: zones.size, domains: rows.length, recs, hosts, mismatch, orphan, external, unbound };
  }, [rows]);

  // Список зон для табов (с числом доменов в каждой).
  const zoneList = useMemo(() => {
    const m = new Map<string, { key: string; name: string; zone_id: string; count: number }>();
    for (const r of rows) {
      const key = r.zone_name || r.zone_id;
      const g = m.get(key) ?? { key, name: r.zone_name || r.zone_id, zone_id: r.zone_id, count: 0 };
      g.count++;
      m.set(key, g);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Счётчики фильтр-табов — в пределах ВЫБРАННОЙ зоны (иначе «Прочее·26» при 1 в зоне).
  const fc = useMemo(() => {
    const scoped = zone === "all" ? rows : rows.filter((r) => (r.zone_name || r.zone_id) === zone);
    let all = scoped.length, hosts = 0, mismatch = 0, orphan = 0, external = 0;
    for (const r of scoped) {
      if (r.endpoint_id) hosts++;
      if (r.status === "mismatch") mismatch++;
      else if (r.status === "orphan") orphan++;
      else if (r.status === "external") external++;
    }
    return { all, hosts, mismatch, orphan, external };
  }, [rows, zone]);

  const matchesFilter = (r: DomainRow) =>
    filter === "all" ? true
    : filter === "hosts" ? !!r.endpoint_id
    : r.status === filter;

  // Зона(ы) → домены, сгруппированные по статусу (проблемы сверху), внутри — по имени.
  const groups = useMemo(() => {
    const byZone = new Map<string, { name: string; zone_id: string; rows: DomainRow[] }>();
    for (const r of rows) {
      const key = r.zone_name || r.zone_id;
      if (zone !== "all" && key !== zone) continue;
      if (!matchesFilter(r)) continue;
      const g = byZone.get(key) ?? { name: r.zone_name || r.zone_id, zone_id: r.zone_id, rows: [] };
      g.rows.push(r);
      byZone.set(key, g);
    }
    return [...byZone.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((g) => {
        // статус-группы внутри зоны
        const st = new Map<Status, DomainRow[]>();
        for (const r of g.rows) (st.get(r.status) ?? st.set(r.status, []).get(r.status)!).push(r);
        const statusGroups = [...st.entries()]
          .sort((a, b) => STATUS[a[0]].order - STATUS[b[0]].order)
          .map(([s, rs]) => ({ status: s, rows: rs.sort((a, b) => a.name.localeCompare(b.name)) }));
        const hosts = g.rows.filter((r) => r.endpoint_id).length;
        const orphans = g.rows.filter((r) => r.status === "orphan").length;
        return { ...g, statusGroups, hosts, orphans, total: g.rows.length };
      });
  }, [rows, zone, filter]);

  const shownCount = groups.reduce((n, g) => n + g.total, 0);

  async function onDelete(r: DomainRow) {
    const live = r.status === "ok" || r.status === "mismatch";
    const ok = await confirmPopup({
      title: `Удалить домен ${r.name}?`,
      body:
        (r.status === "orphan"
          ? "Это «сирота» — запись без хоста. "
          : live
            ? "⚠️ Домен привязан к живому хосту — прокси по нему перестанет резолвиться! "
            : "") + `Снимутся все A-записи (${r.records.length}) из Cloudflare.`,
      confirmLabel: "Удалить",
      danger: true,
    });
    if (!ok) return;
    del.mutate({ name: r.name, zone_id: r.zone_id, force: live });
  }

  const toggleZone = (key: string) =>
    setCollapsed((c) => {
      const n = new Set(c);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const filterTabs: [Filter, string, number, boolean][] = [
    ["all", "Все", fc.all, false],
    ["hosts", "Хосты", fc.hosts, false],
    ["mismatch", "Не туда", fc.mismatch, fc.mismatch > 0],
    ["orphan", "Сироты", fc.orphan, fc.orphan > 0],
    ["external", "Прочее", fc.external, false],
  ];

  const toggleGrp = (key: string) =>
    setCollapsedGrp((c) => {
      const n = new Set(c);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <PageHeader title="Домены" />
      <p className="text-sm text-zinc-500 -mt-2 mb-4 leading-relaxed">
        A-записи наших CF-зон, сопоставленные с хостами и нодами. Сгруппировано по доменам;
        внутри — по статусу (проблемы сверху). Ловит «сирот» — записи на IP наших нод без хоста.
      </p>

      {!q.data?.cf_enabled && (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.05] px-4 py-3 mb-4 text-sm text-amber-300">
          Cloudflare не настроен. Задай токен и зону в{" "}
          <a href="/settings" className="underline">Настройках</a>.
        </div>
      )}

      {/* Инвентарь — сводка по всему парку доменов */}
      <div className="flex flex-wrap gap-2 mb-4">
        <InvChip label="зон" value={inv.zones} />
        <InvChip label="доменов" value={inv.domains} />
        <InvChip label="A-записей" value={inv.recs} />
        <InvChip label="хостов" value={inv.hosts} tone="emerald" />
        {inv.mismatch > 0 && <InvChip label="не туда" value={inv.mismatch} tone="amber" />}
        {inv.orphan > 0 && <InvChip label="сирот" value={inv.orphan} tone="red" />}
        {inv.external > 0 && <InvChip label="прочее" value={inv.external} tone="zinc" />}
        <div className="ml-auto self-center text-xs text-zinc-600">{q.isFetching ? "обновляю…" : ""}</div>
      </div>

      {/* Табы-зоны: scope на один домен, чтобы не скроллить сквозь тысячи саб-доменов */}
      {zoneList.length > 1 && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <ZoneTab active={zone === "all"} onClick={() => setZone("all")} label="Все зоны" count={inv.domains} />
          {zoneList.map((z) => (
            <ZoneTab key={z.key} active={zone === z.key} onClick={() => setZone(z.key)} label={z.name} count={z.count} mono />
          ))}
        </div>
      )}

      {/* Фильтр по статусу */}
      <div className="flex items-center gap-1.5 mb-5 flex-wrap">
        {filterTabs.map(([k, label, n, danger]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-xl text-sm transition ${
              filter === k
                ? "bg-brand-soft text-brand ring-1 ring-inset ring-brand/25 font-semibold"
                : `hover:bg-white/[0.04] ${danger ? "text-red-300" : "text-zinc-400"}`
            }`}
          >
            {label} · {n}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {groups.map((g) => {
          const isCollapsed = zone === "all" && collapsed.has(g.name);
          return (
            <div key={g.zone_id}>
              <button
                onClick={() => zone === "all" && toggleZone(g.name)}
                className={`w-full flex items-center gap-2 mb-2.5 px-0.5 text-left ${zone === "all" ? "cursor-pointer group" : "cursor-default"}`}
              >
                {zone === "all" && (
                  <span className="text-zinc-600 text-xs w-3 group-hover:text-zinc-300 transition">
                    {isCollapsed ? "▸" : "▾"}
                  </span>
                )}
                <span className="text-base">🌐</span>
                <span className="font-mono text-sm text-zinc-200">{g.name}</span>
                <span className="text-[11px] text-zinc-500">· {g.total} доменов</span>
                {g.hosts > 0 && <span className="text-[11px] text-emerald-300/80">· {g.hosts} хостов</span>}
                {g.orphans > 0 && <span className="text-[11px] text-red-300/80">· {g.orphans} сирот</span>}
                <div className="flex-1 h-px bg-white/[0.06] ml-1" />
              </button>

              {!isCollapsed && (
                <div className="space-y-3.5">
                  {g.statusGroups.map((sg) => {
                    const grouped = (filter === "all" || filter === "hosts") && g.statusGroups.length > 1;
                    const gkey = `${g.name}:${sg.status}`;
                    const grpCollapsed = grouped && collapsedGrp.has(gkey);
                    return (
                      <div key={sg.status}>
                        {/* мини-заголовок статус-группы — сворачиваемый (клик по строке) */}
                        {grouped && (
                          <button
                            onClick={() => toggleGrp(gkey)}
                            className="flex items-center gap-1.5 mb-1.5 pl-0.5 group cursor-pointer"
                          >
                            <span className="text-zinc-600 text-[10px] w-3 group-hover:text-zinc-300 transition">
                              {grpCollapsed ? "▸" : "▾"}
                            </span>
                            <span className="text-xs">{STATUS[sg.status].emoji}</span>
                            <span className="text-[11px] uppercase tracking-wider text-zinc-500 group-hover:text-zinc-300 transition" title={STATUS[sg.status].hint}>
                              {STATUS[sg.status].label} · {sg.rows.length}
                            </span>
                          </button>
                        )}
                        {!grpCollapsed && <div className="space-y-2.5">{sg.rows.map((r) => renderRow(r))}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {shownCount === 0 && !q.isLoading && (
          <div className="text-sm text-zinc-600 py-8 text-center">Пусто по этому фильтру.</div>
        )}
      </div>
    </div>
  );

  function renderRow(r: DomainRow) {
    const s = STATUS[r.status];
    return (
      <div
        key={r.name}
        className="glass rounded-2xl border border-white/[0.06] px-4 py-3.5 flex flex-col md:flex-row md:items-center gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-zinc-100 truncate">{r.name}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-lg ring-1 ring-inset ${s.tone}`} title={s.hint}>
              {s.label}
            </span>
            {r.endpoint_name && (
              <span className="text-[11px] text-zinc-500">
                → {r.kind ? `${r.kind} · ` : ""}
                {r.endpoint_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {r.records.map((rec) => (
              <span
                key={rec.id}
                className={`text-[11px] font-mono px-2 py-0.5 rounded-lg ${
                  rec.is_node ? "bg-white/[0.05] text-zinc-300" : "bg-white/[0.03] text-zinc-500"
                }`}
                title={rec.node_name || "не наша нода"}
              >
                {rec.country ? `${flag(rec.country)} ` : ""}
                {rec.ip}
                {rec.node_name ? ` · ${rec.node_name}` : ""}
              </span>
            ))}
            {r.records.length === 0 && <span className="text-[11px] text-zinc-600">нет A-записей</span>}
          </div>
          {r.status === "mismatch" && r.expected_ips.length > 0 && (
            <div className="text-[11px] text-amber-400/80 mt-1">ожидается: {r.expected_ips.join(", ")}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {r.records.length > 1 && <Pill tone="neutral">{r.records.length}× RR</Pill>}
          <Button
            variant="ghost"
            className="text-red-400 hover:bg-red-500/10"
            disabled={del.isPending || r.records.length === 0}
            onClick={() => onDelete(r)}
          >
            Удалить
          </Button>
        </div>
      </div>
    );
  }
}

function InvChip({ label, value, tone = "zinc" }: { label: string; value: number; tone?: "zinc" | "emerald" | "amber" | "red" }) {
  const tones: Record<string, string> = {
    zinc: "text-zinc-300",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    red: "text-red-300",
  };
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-1.5 flex items-baseline gap-1.5">
      <span className={`text-sm font-semibold ${tones[tone]}`}>{value}</span>
      <span className="text-[11px] text-zinc-500">{label}</span>
    </div>
  );
}

function ZoneTab({ active, onClick, label, count, mono }: { active: boolean; onClick: () => void; label: string; count: number; mono?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-xl text-sm transition flex items-center gap-1.5 ${
        active ? "bg-white/[0.07] text-zinc-100 ring-1 ring-inset ring-white/10 font-medium" : "text-zinc-400 hover:bg-white/[0.04]"
      }`}
    >
      <span className={mono ? "font-mono text-[13px]" : ""}>{label}</span>
      <span className="text-[11px] text-zinc-500">{count}</span>
    </button>
  );
}
